import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Building2, Layers, Plus, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

export default function CompaniesDivisions() {
  const qc = useQueryClient();
  const companiesQ = useQuery({ queryKey: ["companies"], queryFn: () => api.get("/administration/companies").then((r) => r.data) });
  const divisionsQ = useQuery({ queryKey: ["divisions"], queryFn: () => api.get("/administration/divisions").then((r) => r.data) });

  const [companyDialog, setCompanyDialog] = useState({ open: false, edit: null });
  const [divisionDialog, setDivisionDialog] = useState({ open: false, edit: null });

  const companyMut = useMutation({
    mutationFn: async (payload) => {
      if (payload.id) {
        const { id, ...body } = payload;
        return api.patch(`/administration/companies/${id}`, body).then((r) => r.data);
      }
      return api.post("/administration/companies", payload).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      toast.success("Saved");
      setCompanyDialog({ open: false, edit: null });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"),
  });

  const divisionMut = useMutation({
    mutationFn: async (payload) => {
      if (payload.id) {
        const { id, ...body } = payload;
        return api.patch(`/administration/divisions/${id}`, body).then((r) => r.data);
      }
      return api.post("/administration/divisions", payload).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["divisions"] });
      toast.success("Saved");
      setDivisionDialog({ open: false, edit: null });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"),
  });

  const teamManagersQ = useQuery({ queryKey: ["team-managers"], queryFn: () => api.get("/administration/team-managers").then((r) => r.data) });
  const grantMut = useMutation({
    mutationFn: ({ user_id, ...body }) => api.patch(`/administration/users/${user_id}/scope`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team-managers"] });
      toast.success("Authorization updated");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"),
  });

  const companies = companiesQ.data || [];
  const divisions = divisionsQ.data || [];
  const divByCompany = useMemo(() => {
    const m = {};
    for (const d of divisions) (m[d.company_id] ||= []).push(d);
    return m;
  }, [divisions]);

  return (
    <div className="space-y-6" data-testid="companies-divisions-page">
      <PageHeader
        title="Companies & Business Divisions"
        subtitle="Manage the legal entities you operate and the business divisions inside each (Architectural, Structural, …)."
      />

      <div className="flex justify-end">
        <Button onClick={() => setCompanyDialog({ open: true, edit: null })} data-testid="add-company-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Company
        </Button>
      </div>

      {/* Team Manager quotation authorization panel */}
      <Card className="p-5" data-testid="team-manager-quotation-panel">
        <div className="mb-3">
          <h3 className="text-base font-semibold text-zinc-900">Team Manager Quotation Authorization</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            Enable quotation preparation for selected Team Managers. Enabled users can view / create / edit / submit-for-approval quotations for their assigned clients only. Director retains sole authority to approve and send to client.
          </p>
        </div>
        <div className="divide-y divide-zinc-100">
          {(teamManagersQ.data || []).map((tm) => (
            <div key={tm.user_id} className="flex items-center justify-between py-3" data-testid={`team-manager-row-${tm.email}`}>
              <div>
                <div className="text-sm font-medium text-zinc-900">{tm.name}</div>
                <div className="text-xs text-zinc-500">{tm.email}</div>
              </div>
              <Button
                size="sm"
                variant={tm.can_prepare_quotations ? "default" : "outline"}
                onClick={() => grantMut.mutate({ user_id: tm.user_id, can_prepare_quotations: !tm.can_prepare_quotations })}
                data-testid={`toggle-can-prepare-${tm.email}`}
              >
                {tm.can_prepare_quotations ? (
                  <><ToggleRight className="w-4 h-4 mr-1.5 text-white" /> Quotation-prep enabled</>
                ) : (
                  <><ToggleLeft className="w-4 h-4 mr-1.5" /> Enable quotation preparation</>
                )}
              </Button>
            </div>
          ))}
          {!(teamManagersQ.data || []).length && (
            <div className="text-xs text-zinc-400 italic py-3">No Team Manager users found in this organization.</div>
          )}
        </div>
      </Card>

      <div className="grid gap-4">
        {companies.map((c) => (
          <Card key={c.id} className="p-5" data-testid={`company-card-${c.code}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-zinc-500" />
                  <h3 className="text-base font-semibold text-zinc-900">{c.name}</h3>
                  <Badge variant="outline" className="text-[10px] font-mono">{c.code}</Badge>
                  {!c.active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {c.legal_name || "—"} · GSTIN {c.gstin || "—"} · {c.country} · {c.currency}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm" variant="ghost"
                  onClick={() => companyMut.mutate({ id: c.id, active: !c.active })}
                  data-testid={`toggle-company-${c.code}`}
                >
                  {c.active ? <ToggleRight className="w-4 h-4 text-emerald-600" /> : <ToggleLeft className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setCompanyDialog({ open: true, edit: c })} data-testid={`edit-company-${c.code}`}>Edit</Button>
              </div>
            </div>

            <div className="mt-4 pl-6 border-l-2 border-zinc-100">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-medium text-zinc-600 uppercase tracking-wider flex items-center gap-1.5">
                  <Layers className="w-3 h-3" /> Business Divisions
                </h4>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => setDivisionDialog({ open: true, edit: { company_id: c.id } })}
                  data-testid={`add-division-${c.code}`}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Division
                </Button>
              </div>
              <div className="space-y-2">
                {(divByCompany[c.id] || []).map((d) => (
                  <div key={d.id} className="flex items-center justify-between text-sm py-1.5" data-testid={`division-row-${d.code}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-zinc-800">{d.name}</span>
                      <Badge variant="outline" className="text-[10px] font-mono">{d.code}</Badge>
                      {!d.active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                      {d.description && <span className="text-xs text-zinc-500">· {d.description}</span>}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => divisionMut.mutate({ id: d.id, active: !d.active })}
                        data-testid={`toggle-division-${d.code}`}
                      >
                        {d.active ? <ToggleRight className="w-4 h-4 text-emerald-600" /> : <ToggleLeft className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDivisionDialog({ open: true, edit: d })} data-testid={`edit-division-${d.code}`}>Edit</Button>
                    </div>
                  </div>
                ))}
                {!(divByCompany[c.id] || []).length && (
                  <div className="text-xs text-zinc-400 italic">No divisions yet.</div>
                )}
              </div>
            </div>
          </Card>
        ))}
        {!companies.length && (
          <div className="text-sm text-zinc-500 italic">No companies configured yet.</div>
        )}
      </div>

      {/* Company dialog */}
      <CompanyDialog dialog={companyDialog} onClose={() => setCompanyDialog({ open: false, edit: null })} onSave={(v) => companyMut.mutate(v)} />
      {/* Division dialog */}
      <DivisionDialog dialog={divisionDialog} companies={companies} onClose={() => setDivisionDialog({ open: false, edit: null })} onSave={(v) => divisionMut.mutate(v)} />
    </div>
  );
}

function CompanyDialog({ dialog, onClose, onSave }) {
  const [form, setForm] = useState({});
  const edit = dialog.edit;
  const initial = useMemo(() => edit || { code: "", name: "", legal_name: "", gstin: "", country: "IN", currency: "INR", address: "" }, [edit]);

  return (
    <Dialog open={dialog.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{edit ? "Edit Company" : "Add Company"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!edit && (
            <div>
              <Label>Code</Label>
              <Input onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. DHE" data-testid="company-code-input" />
            </div>
          )}
          <div>
            <Label>Name</Label>
            <Input defaultValue={initial.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" />
          </div>
          <div>
            <Label>Legal Name</Label>
            <Input defaultValue={initial.legal_name || ""} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
          </div>
          <div>
            <Label>GSTIN</Label>
            <Input defaultValue={initial.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Country</Label>
              <Input defaultValue={initial.country || "IN"} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </div>
            <div>
              <Label>Currency</Label>
              <Input defaultValue={initial.currency || "INR"} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Address</Label>
            <Textarea defaultValue={initial.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(edit ? { id: edit.id, ...form } : { ...initial, ...form })}
            data-testid="save-company-btn"
          >Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DivisionDialog({ dialog, companies, onClose, onSave }) {
  const [form, setForm] = useState({});
  const edit = dialog.edit || {};
  const isEdit = !!edit.id;
  const initial = useMemo(() => edit || {}, [edit]);

  return (
    <Dialog open={dialog.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Division" : "Add Business Division"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!isEdit && (
            <>
              <div>
                <Label>Company</Label>
                <select className="w-full border rounded-md px-3 py-2 text-sm" defaultValue={initial.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} data-testid="division-company-select">
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Code</Label>
                <Input placeholder="e.g. arch, struct" onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })} data-testid="division-code-input" />
              </div>
            </>
          )}
          <div>
            <Label>Name</Label>
            <Input defaultValue={initial.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="division-name-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea defaultValue={initial.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(isEdit ? { id: edit.id, ...form } : { company_id: initial.company_id || companies[0]?.id, ...form })} data-testid="save-division-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
